import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Send } from "lucide-react";
import { toast } from "sonner";

const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

export function StatementSection({ propertyId, ownerEmail }) {
  const [month, setMonth] = useState(lastMonth());
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (!propertyId) return;
    api.get(`/statements/notes?property_id=${propertyId}&month=${month}`).then((r) => setNotes(r.data.notes || "")).catch(() => {});
  }, [propertyId, month]);
  const loadHistory = () => api.get(`/statements/${propertyId}/history`).then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { if (propertyId) loadHistory(); /* eslint-disable-next-line */ }, [propertyId]);
  if (!propertyId) return null;
  const saveNotes = () => api.put("/statements/notes", { property_id: propertyId, month, notes });
  const preview = async () => {
    setBusy("preview");
    try {
      await saveNotes();
      const r = await api.get(`/statements/${propertyId}/pdf?month=${month}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };
  const send = async () => {
    if (!window.confirm(`Email the ${month} statement to ${ownerEmail}?`)) return;
    setBusy("send");
    try {
      await saveNotes();
      const r = await api.post(`/statements/${propertyId}/send`, { month });
      r.data.ok ? toast.success(`Statement sent to ${r.data.owner_email}`) : toast.error("Email provider rejected the send");
      loadHistory();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };
  return (
    <div className="pt-4 border-t border-slate-100" data-testid="properties-statement-section">
      <Label className="ns-label">Landlord profit statement</Label>
      <div className="grid grid-cols-[0.8fr_auto_auto] gap-2 items-end mt-2">
        <Input type="month" className="h-9 bg-white text-xs" value={month} data-testid="properties-statement-month" onChange={(e) => setMonth(e.target.value)} />
        <button type="button" onClick={preview} disabled={!!busy} data-testid="properties-statement-preview"
          className="h-9 px-3 rounded-full border border-slate-200 text-xs inline-flex items-center gap-1.5 hover:bg-slate-50 transition-colors disabled:opacity-50">
          <FileText className="w-3.5 h-3.5" /> {busy === "preview" ? "Building…" : "Preview PDF"}
        </button>
        <button type="button" onClick={send} disabled={!!busy || !ownerEmail} data-testid="properties-statement-send"
          title={ownerEmail ? "" : "Add a landlord email above first"}
          className="h-9 px-3 rounded-full bg-teal-700 text-white text-xs inline-flex items-center gap-1.5 hover:bg-teal-800 transition-colors disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed">
          <Send className="w-3.5 h-3.5" /> {busy === "send" ? "Sending…" : "Send now"}
        </button>
      </div>
      <Textarea rows={3} className="mt-2 bg-white text-xs" placeholder="Notes for this month's statement (repairs, voids, upcoming works…)"
        value={notes} data-testid="properties-statement-notes" onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} />
      {!ownerEmail && <div className="text-[11px] text-amber-700 mt-1">Add a landlord email to enable sending.</div>}
      {history.length > 0 && (
        <ul className="mt-2 text-[11px] text-slate-500" data-testid="properties-statement-history">
          {history.slice(0, 4).map((h) => (
            <li key={h.id}>{h.month} → {h.owner_email} · profit £{h.net.toFixed(2)} · {h.ok ? "sent" : "failed"} {h.sent_at.slice(0, 10)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
